import React, { useEffect } from 'react';
import { useFormik } from 'formik';
import {
  Grid,
  TextField,
  Button,
  Chip,
  LinearProgress,
} from '@mui/material';
import { useClient } from 'shared/apollo';
import Autocomplete from '@mui/lab/Autocomplete';
import { Text } from 'shared/components';
import { useServiceByIdQuery } from './useServiceByIdQuery';
import { usePorfoliosQuery } from '../ServiceCollections/queries';
import styled from 'styled-components';
import { FullWidthField } from 'shared/account/components/FormField';
import { getPortfolio } from './api.ts';
const STAGE_NAME_USER_FRIENDLY = {
  'Capacity Developed': 'Capacity Developed',
  'Reached': 'Reach',
  'Prepared': 'Learn',
  'Mobilized': 'Act',
  'Social Impact': 'Social Impact',
};

const ErrorLabel = ({ error }) => {
  if (!error) null;
  return (
    <Grid items xs={12}>
      <Error>{error}</Error>
    </Grid>
  );
};

const ServiceDrawer = ({
  collections,
  indicators,
  indicatorStages,
  donors,
  donorLoad,
  onSave,
  dbId,
  isNewLoading,
  isDeleteLoading,
  isUpdateLoading,
  requestDelete,
}) => {
  const data = useServiceByIdQuery(dbId);
  const client = useClient();
  const { values, setFieldValue, errors, handleSubmit, ...rest } = useFormik({
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      name: '',
      description: '',
      collectionIds: [],
      portfolios: [],
    },
    onSubmit: (values, { resetForm }) => {
      const { name, description, collectionIds, ...indicatorStagesIds } = values;
      const indicatorIds = Object.keys(indicatorStagesIds).reduce((acc, key) => {
        acc = [...acc, ...indicatorStagesIds[key].map(ind => ind.id)];
        return acc;
      }, []);
      const form = {
        id: dbId,
        name,
        description,
        collectionIds: JSON.stringify(collectionIds.map(item => item.dbId)),
        indicatorIds: JSON.stringify(indicatorIds),
      };
      const end = values.portfolios.map(item => item.portfolioId || item.dbId) || [];

      const removedPortfolios = difference(data?.portfolios || [], end);
      const addedPortfolios = difference(end, data?.portfolios || []);
      onSave(form, addedPortfolios, removedPortfolios);
    },
    validate: (values) => {
      const { name, description, collectionIds, donors, portfolios, ...stages } = values;
      const errors = {};
      if (!values.name) {
        errors.name = 'A name is required';
      }
      if (!values.description) {
        errors.description = 'A stage is required';
      }
      if (!values.collectionIds || values.collectionIds.length === 0) {
        errors.collectionIds = 'At least one cause category is required';
      }
      const indicatorsSelected = Object.keys(stages).reduce((acc, key) => {
        acc += stages[key].length;
        return acc;
      }, 0);
      if (!indicatorsSelected) {
        errors.indicatorsSelected = 'At least one indicator is required';
      }
      return errors;
    }
  });

  const handleDelete = () => {
    requestDelete(dbId);
  };
  const donorsIds = (values?.donors || []).map(({ dbId }) => dbId);
  const { data: portfolios, refresh: refreshPortfolios } = usePorfoliosQuery(donorsIds);

  useEffect(() => {
    if (data?.dbId) {
      const {
        name,
        description,
        collections: selectedCollections,
        indicators: selectedIndicators,
      } = data;
      setFieldValue('name', name);
      setFieldValue('description', description);
      setFieldValue('collectionIds', selectedCollections);
      indicatorStages.map(({ dbId: stageKey }) => {
        const inticatorsInStage = selectedIndicators.filter(item => item.stageId === stageKey).map(item => ({
          name: item.name,
          id: item.dbId,
        }));
        if (inticatorsInStage && inticatorsInStage.length > 0) {
          setFieldValue(stageKey, inticatorsInStage);
        }
      });
      if (!data?.portfolios) return;
      let displayPorts = data.portfolios.map(id => getPortfolio(client, id));
      Promise.all(displayPorts).then(ports => {
        setFieldValue('portfolios', ports.map(port => ({ dbId: port.portfolioId, name: port.name, accountId: port.accountId })));
      });
      const portfolioIds = values.portfolios.map(({ accountId }) => accountId);
      setFieldValue('donors', donors.filter(donor => portfolioIds.includes(donor.dbId)));
    }
  }, [data]);

  useEffect(() => {
    const portfolioIds = values.portfolios.map(({ accountId }) => accountId);
    setFieldValue('donors', donors.filter(donor => portfolioIds.includes(donor.dbId)));
  }, [values.portfolios]);

  if (!data) {
    return <Grid items xs={12}><LinearProgress variant="query" /></Grid>;
  }

  const IndicatorsSection = ({ stage: { dbId: id, name } }) => {
    const availableIndicators = indicators.filter(({ stageId }) => stageId === id).map(stage => ({ id: stage.dbId, name: stage.name }));
    return (
      <React.Fragment>
        <Grid items xs={3}>{STAGE_NAME_USER_FRIENDLY[name]}</Grid>
        <Grid items xs={9}>
          <Autocomplete
            options={availableIndicators}
            value={values[id]}
            onChange={(_, value) => setFieldValue(id, value)}
            getOptionLabel={option => option.name}
            renderInput={params => (
              <TextField {...params} variant="outlined" label={`Selected: ${(values[id] || []).length}`} />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (<Chip label={option.name} {...getTagProps({ index })} />))
            }
            multiple
          />
        </Grid>
      </React.Fragment>
    );
  };

  return (
    <Grid container direction="row" justify="center" alignItems="center">
      {(isNewLoading || isUpdateLoading || isDeleteLoading) &&
        <LinearProgress variant="query" />}
      {dbId && (
        <Grid item xs={12} style={{ marginTop: '10px' }}>
          <Text variant="body1">ID: <b>{dbId}</b></Text>
        </Grid>
      )}
      <FullWidthField label="Name" name="name" {...{ values, errors, setFieldValue }} />
      <FullWidthField label="Description" name="description" {...{ values, errors, setFieldValue }} multiline />
      <Grid items xs={12}>
        Add to Cause Categories:
      </Grid>
      {(collections && (<Grid item xs={12}>
        <Autocomplete
          options={collections}
          value={values.collectionIds}
          onChange={(_, value) => setFieldValue('collectionIds', value)}
          getOptionLabel={option => option.name}
          renderInput={params => (
            <TextField {...params} variant="outlined" label={`Selected: ${(values?.collectionIds || []).length}`} />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (<Chip label={option.name} {...getTagProps({ index })} />))
          }
          multiple
        />
      </Grid>)) ||
        <Grid items xs={12}>
          Loading...
        </Grid>
      }
      <ErrorLabel error={errors.collectionIds} />
      <Grid items xs={12}>
        Associate Portfolios:
      </Grid>
      <Grid items xs={12} style={{ marginLeft: '10px', fontSize: '.8rem' }}>
        Note: Donor Selection is only used to lookup portfolios, selected donors are dependent on portfolio selection.
        {<Grid item xs={12}>
          <Autocomplete
            options={donors}
            value={values.donors}
            onChange={(_, value) => {
              setFieldValue('donors', value);
              refreshPortfolios();
            }}

            getOptionLabel={({ name, dbId, userId, adminEmail }) => `${name} - ${adminEmail} (ID: ${dbId})`}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                label={values.donors ? `Donors: ${values.donors?.length} selected` : 'Donors: 0 selected'}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map(({ name, dbId }, index) => (
                <Chip color='primary' label={`${name} (ID: ${dbId})`} {...getTagProps({ index })} />
              ))
            }
            noOptionsText={donorLoad ? 'Loading...' : 'No Options'}
            multiple
          />
        </Grid>}
        {<Grid item xs={12}>
          <Autocomplete
            options={portfolios}
            value={values.portfolios}
            onChange={(_, value) => {
              const withoutDuplicates = withoutDuplicatePairs(value);
              if (withoutDuplicates.length === value.length) {
                setFieldValue('portfolios', value);
              } else {
                setFieldValue('portfolios', withoutDuplicates);
              }
            }}
            getOptionLabel={({ name, portfolioId }) => `${name} - (ID: ${portfolioId})`}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                label={`Portfolios: ${values.portfolios?.length} selected`}
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map(({ name }, index) => (
                <Chip color='primary' label={name} {...getTagProps({ index })} />
              ))
            }
            noOptionsText={values.donor ? 'No Options' : 'Try Selecting a Donor to Search Under'}
            multiple
          />
        </Grid>}
      </Grid>


      <Grid items xs={12}>
        Indicators:
      </Grid>
      {(indicatorStages || []).map(stage => <IndicatorsSection key={stage.dbId} stage={stage} />)}
      <ErrorLabel error={errors.indicatorsSelected} />
      <Grid item xs={6} style={{ marginTop: '10px' }}>
        {dbId && (
          <Button
            onClick={handleDelete}
            style={{ float: 'left' }}
            disabled={isDeleteLoading}
          >
            DELETE SERVICE
          </Button>
        )}
      </Grid>
      <Grid item xs={6} style={{ marginTop: '10px', marginBottom: '20px' }}>
        <Button
          onClick={handleSubmit}
          style={{ float: 'right' }}
          disabled={isNewLoading || isUpdateLoading}
        >
          SAVE CHANGES
        </Button>
      </Grid>
    </Grid>
  );
};

const Error = styled.label`
  color: #f44336;
  font-size: 0.75rem;
  margin-left: 14px;
`;
const difference = (arr1, arr2) => {
  return arr1.filter(x => !arr2.includes(x));
};

function withoutDuplicatePairs(arr) {
  const pairCount = new Map();
  for (const obj of arr) {
    const key = JSON.stringify(Object.values(obj));
    pairCount.set(key, (pairCount.get(key) || 0) + 1);
  }

  return arr.filter(obj => {
    const key = JSON.stringify(Object.values(obj));
    return pairCount.get(key) === 1;
  });
}
export default ServiceDrawer;
